import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import { api, formatApiError, formatDate } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Trash2, Eye,
} from "lucide-react";

export default function AdminPromptHistoryPage() {
  const { promptKey } = useParams();
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingContent, setViewingContent] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/prompts/${promptKey}/history`);
      setVersions(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando historial");
    } finally {
      setLoading(false);
    }
  }, [promptKey]);

  useEffect(() => { load(); }, [load]);

  const activate = async (promptId) => {
    try {
      await api.post(`/admin/prompts/${promptId}/activate`);
      toast.success("Versión activada");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error activando");
    }
  };

  const deleteVersion = async (id) => {
    try {
      await api.delete(`/admin/prompts/${id}`);
      toast.success("Versión eliminada");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error eliminando");
      setConfirmDelete(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-color-mode="light">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/prompts/${promptKey}`)} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al editor
        </Button>
        <h1 className="font-serif text-3xl text-gray-900 mb-1">Historial de versiones</h1>
        <code className="text-xs bg-gray-100 px-1.5 py-0.5">{promptKey}</code>
      </div>

      <div className="space-y-3">
        {loading && <div className="p-8 text-center text-gray-500">Cargando…</div>}
        {!loading && versions.length === 0 && (
          <div className="p-8 text-center text-gray-500 border border-dashed border-gray-200 rounded-sm">
            Sin versiones.
          </div>
        )}
        {versions.map((v) => (
          <div
            key={v.id}
            className={`border rounded-sm p-4 transition-colors ${
              v.is_active ? "border-emerald-300 bg-emerald-50/30" : "border-gray-200 bg-white"
            }`}
            data-testid={`version-${v.version}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-gray-900">Versión {v.version}</span>
                  {v.is_active && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-sm border border-emerald-200 font-semibold">
                      <CheckCircle2 className="w-3 h-3" /> Activa
                    </span>
                  )}
                  <span className="text-[10px] text-gray-400 font-mono">hash: {v.hash}</span>
                </div>
                <div className="text-xs text-gray-500 flex gap-3 flex-wrap">
                  <span>Modelo: {v.model || "—"}</span>
                  <span>·</span>
                  <span>{(v.content || "").length} chars / {(v.content || "").split("\n").length} líneas</span>
                  <span>·</span>
                  <span>{formatDate(v.updated_at)}</span>
                  {v.updated_by && <><span>·</span><span>por {v.updated_by.slice(0, 8)}</span></>}
                </div>
                {v.description && (
                  <p className="text-xs text-gray-600 mt-2 line-clamp-2">{v.description}</p>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => setViewingContent(v)}>
                  <Eye className="w-4 h-4 mr-1" /> Ver
                </Button>
                {!v.is_active && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => activate(v.id)}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Activar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDelete(v)}
                      className="text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal ver contenido */}
      <Dialog open={!!viewingContent} onOpenChange={(o) => !o && setViewingContent(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {viewingContent && (
            <>
              <DialogHeader>
                <DialogTitle>Versión {viewingContent.version}</DialogTitle>
                <DialogDescription>
                  {viewingContent.is_active && "Versión actualmente activa · "}
                  {formatDate(viewingContent.updated_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 max-h-[60vh] overflow-y-auto">
                <MDEditor.Markdown source={viewingContent.content} style={{ background: "white", padding: 16 }} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingContent(null)}>Cerrar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal confirmar eliminación */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar versión {confirmDelete?.version}</DialogTitle>
            <DialogDescription>
              ¿Seguro que quieres eliminar esta versión? La acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button
              onClick={() => deleteVersion(confirmDelete.id)}
              className="bg-red-700 hover:bg-red-800 text-white"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
